import React, { useState, useEffect } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Task } from '../../../services/tasks';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperclip, 
  faCommentDots, 
  faSquareCheck, 
  faCalendarDays,
} from '@fortawesome/pro-solid-svg-icons';
import { colors } from '../../../theme/colors';
import { priorityChipBaseStyle, priorityChipStyles, tagChipStyle } from '../plannerVisuals';

interface TaskCardProps {
  task: Task;
  onClick?: (e: React.MouseEvent) => void;
  selected?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onClick, selected = false }) => {
  const [hasDragged, setHasDragged] = useState(false);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: task.id,
    data: { task },
    disabled: selected, // Disable drag when in selection mode
  });

  const style = {
    transform: isDragging ? undefined : CSS.Translate.toString(transform),
    opacity: isDragging ? 0 : 1,
    pointerEvents: isDragging ? ('none' as const) : ('auto' as const),
    width: '100%',
    minHeight: '104px',
    flexShrink: 0,
    background: '#09090A',
    borderRadius: 10,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    overflow: 'hidden',
    cursor: isDragging ? 'grabbing' : 'grab',
    marginBottom: 0,
    border: isDragging ? `1px solid ${colors.primary}` : '1px solid transparent',
    padding: '14px',
    transition: isDragging ? 'none' : 'background 0.16s ease',
    position: 'relative' as const,
    zIndex: isDragging ? 1000 : 1,
  };

  const isOverdue = task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day');
  const isDueSoon = task.dueDate && dayjs(task.dueDate).diff(dayjs(), 'day') <= 2 && !isOverdue;
  
  // Calculate checklist progress
  const checklist = task.checklist || [];
  const completedCount = checklist.filter((item: any) => item.completed).length;
  const checklistProgress = checklist.length > 0 ? (completedCount / checklist.length) * 100 : 0;
  
  // Get counts
  const commentsCount = (task as any).commentsCount || 0;
  const attachmentsCount = (task as any).attachmentsCount || (task.attachments?.length || 0);

  // Separate drag handle to avoid conflicts with click
  const dragHandleProps = !selected && !isDragging ? { ...listeners, ...attributes } : {};

  // Track if we actually dragged (not just clicked)
  useEffect(() => {
    if (isDragging) {
      setHasDragged(true);
    } else if (!isDragging && hasDragged) {
      // Reset after drag ends
      const timer = setTimeout(() => setHasDragged(false), 100);
      return () => clearTimeout(timer);
    }
  }, [isDragging, hasDragged]);

  return (
    <div 
      ref={setNodeRef} 
      style={{
        ...style,
        border: selected ? `1px solid ${colors.primary}` : style.border,
        background: selected ? '#171016' : style.background,
      }} 
      {...dragHandleProps}
      onClick={(e) => {
        // Only trigger onClick if we didn't drag
        if (!isDragging && !hasDragged && onClick) {
          onClick(e);
        }
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = selected ? '#1D121B' : '#0F0F11';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = selected ? '#171016' : '#09090A';
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 12,
          marginBottom: 8,
        }}
      >
        <div
          style={{
            color: colors.textPrimary,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '1.4',
            flex: 1,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {task.title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              ...priorityChipBaseStyle,
              ...(priorityChipStyles[task.priority] || priorityChipStyles.MEDIUM),
            }}
          >
            {task.priority}
          </div>
        </div>
      </div>

      {/* Description */}
          {task.description && (
            <div style={{
          color: colors.textMuted,
          fontSize: 12,
          marginBottom: 8,
              overflow: 'hidden',
              display: '-webkit-box',
          WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
          lineHeight: '1.4',
            }}>
              {task.description}
            </div>
      )}

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6,
          marginBottom: 8,
          maxWidth: '100%',
        }}>
          {task.tags.slice(0, 3).map((tag: string, idx: number) => (
            <span
              key={idx}
              style={{
                ...tagChipStyle,
                minHeight: 22,
                fontSize: 10,
                padding: '1px 7px',
              }}
            >
              {tag}
            </span>
          ))}
          {task.tags.length > 3 && (
            <span
              style={{
                minHeight: 22,
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: 10,
                lineHeight: 1,
                color: colors.textMuted,
              }}
            >
              +{task.tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Checklist Progress */}
      {checklist.length > 0 && (
        <div style={{ width: '100%', marginBottom: 8 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 6, 
            marginBottom: 4,
            fontSize: 11,
            color: colors.textMuted,
          }}>
            <FontAwesomeIcon icon={faSquareCheck} style={{ fontSize: 12 }} />
            <span>{completedCount}/{checklist.length}</span>
          </div>
          <div style={{
            width: '100%',
            height: 4,
            background: colors.bgElevated,
            borderRadius: 2,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${checklistProgress}%`,
              height: '100%',
              background: '#DB50AC',
              transition: 'width 0.3s',
            }} />
              </div>
              </div>
      )}

      {/* Footer: Icons and Metadata */}
      <div style={{ 
        width: '100%', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginTop: 'auto',
        paddingTop: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Due Date */}
          {task.dueDate && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 4,
              fontSize: 11,
              color: isOverdue ? colors.error : isDueSoon ? '#FF9800' : colors.textMuted,
            }}>
              <FontAwesomeIcon icon={faCalendarDays} style={{ fontSize: 12 }} />
              <span>{dayjs(task.dueDate).format('MMM D')}</span>
            </div>
          )}

          {/* Attachments */}
          {attachmentsCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: colors.textMuted }}>
              <FontAwesomeIcon icon={faPaperclip} style={{ fontSize: 12 }} />
              <span>{attachmentsCount}</span>
          </div>
          )}
          
          {/* Comments */}
          {commentsCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: colors.textMuted }}>
              <FontAwesomeIcon icon={faCommentDots} style={{ fontSize: 12 }} />
              <span>{commentsCount}</span>
            </div>
          )}
        </div>

        {/* Assignee Avatar */}
        {task.assignee && (
          <div style={{ 
            width: 24, 
            height: 24, 
            borderRadius: '50%', 
            overflow: 'hidden',
            border: `2px solid ${colors.primary}`,
          }}>
            {task.assignee.photoUrl ? (
              <img 
                src={task.assignee.photoUrl} 
                alt={task.assignee.name} 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
              />
            ) : (
              <div style={{ 
                width: '100%', 
                height: '100%', 
                background: colors.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: 10,
                fontWeight: 600,
              }}>
                {task.assignee.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskCard;
